import {
  type BaseConfig,
  sendLinkEvent,
  sendViewEvent,
} from '@seek/libs-shared';

export type ReviewFormActionOrigin =
  | 'Company Profile'
  | 'Candidate Profile'
  | 'Companies'
  | 'Career Advice'
  | null;

export interface ReviewFormContext {
  reviewId: string;
  reviewFormActionOrigin?: ReviewFormActionOrigin;
  variant?: string;
}

interface CommonInputType {
  config: BaseConfig;
  context: ReviewFormContext;
}

interface ValidationErrorCommonInputType {
  errorMessage: string[];
  errorText: string[];
}

interface ContinueValidationCommonInputType {
  validation: 'mandatory_questions_not_answered' | 'success';
}

const sendViewEventWithCommonProps = ({
  eventName,
  config,
  context,
  trackingProps,
}: {
  config: BaseConfig;
  context: ReviewFormContext;
  eventName: string;
  trackingProps: Record<string, unknown>;
}) => {
  sendViewEvent(eventName, config, {
    ...trackingProps,
    ...context,
  });
};

const sendLinkEventWithCommonProps = ({
  eventName,
  config,
  context,
  trackingProps,
}: {
  config: BaseConfig;
  context: ReviewFormContext;
  eventName: string;
  trackingProps: Record<string, unknown>;
}) => {
  sendLinkEvent(eventName, config, {
    ...trackingProps,
    ...context,
  });
};

// Company Intercept Page

const companyInterceptPageCommonTrackingProps = () => ({
  currentPage: 'cr_company_intercept',
});

export const trackDisplayCompanyInterceptPage = ({
  config,
  context,
}: CommonInputType) => {
  sendViewEventWithCommonProps({
    eventName: 'cr_company_intercept_displayed',
    config,
    context,
    trackingProps: {
      ...companyInterceptPageCommonTrackingProps(),
    },
  });
};

export const trackClickCompanyInterceptSelection = ({
  config,
  context,
  selectedCompanyId,
  cardPosition,
}: CommonInputType & {
  cardPosition: number;
  selectedCompanyId: string;
}) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_selection_pressed',
    config,
    context,
    trackingProps: {
      ...companyInterceptPageCommonTrackingProps(),
      companyId: selectedCompanyId,
      cardPosition,
    },
  });
};

export const trackClickCompanyInterceptReviewDifferentCompany = ({
  config,
}: CommonInputType) => {
  sendLinkEvent(
    'cr_company_intercept_review_different_company_pressed',
    config,
    {
      ...companyInterceptPageCommonTrackingProps(),
    },
  );
};

// Overall Page

interface VariantType {
  variant: string;
}

interface IsSimplifiedReviewFormType {
  isSimplifiedReviewForm: boolean;
}

interface OveralPageSubSectionInputType {
  currentPageSubsection: 'company_name' | 'overall_experience';
}

const overallPageCommonTrackingProps = () => ({
  currentPage: CURRENT_PAGE_OVERALL,
});

const experimentTrackingProps = ({ variant }: VariantType) => ({
  hubbleExperiments: {
    '202505-single-page-review-flow': {
      id: 'ab1f0170-eb9b-421e-beb0-861c91743539',
      name: '202505-single-page-review-flow',
      participantStatus: 'enrolled',
      variant,
    },
  },
});

const experimentTwoTrackingProps = ({ variant }: VariantType) => ({
  hubbleExperiments: {
    '202508-company-review-form-experiment-2': {
      name: '202508-company-review-form-experiment-2',
      participantStatus: 'enrolled',
      variant,
    },
  },
});

const isSimplifiedReviewFormTrackingProps = ({
  isSimplifiedReviewForm,
}: IsSimplifiedReviewFormType) => ({
  currentPage: isSimplifiedReviewForm
    ? 'cr_write_a_review_simplified_page'
    : 'cr_write_a_review_overall_rating',
});

export const trackWriteAReviewStarted = ({
  config,
  context,
  variant,
  isSimplifiedReviewForm,
}: CommonInputType & VariantType & IsSimplifiedReviewFormType) => {
  sendViewEventWithCommonProps({
    eventName: 'cr_write_a_review_started',
    config,
    context,
    trackingProps: {
      ...experimentTrackingProps({ variant }),
      ...isSimplifiedReviewFormTrackingProps({ isSimplifiedReviewForm }),
    },
  });
};

export const trackClickOverallPageContinue = ({
  config,
  context,
}: CommonInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_write_a_review_overall_rating_continue_pressed',
    config,
    context,
    trackingProps: {
      ...overallPageCommonTrackingProps(),
    },
  });
};

export const trackClickOverallPageSubSection = ({
  config,
  context,
  currentPageSubsection,
}: CommonInputType & OveralPageSubSectionInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_selection_pressed',
    config,
    context,
    trackingProps: {
      ...overallPageCommonTrackingProps(),
      currentPageSubsection,
    },
  });
};

export const trackValidationErrorOverallPage = ({
  config,
  context,
  errorMessage,
  errorText,
}: ValidationErrorCommonInputType & CommonInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_validation_error',
    config,
    context,
    trackingProps: {
      ...overallPageCommonTrackingProps(),
      errorMessage,
      errorText,
    },
  });
};

// Your Role Page

interface YourRolePageCommonInputType extends CommonInputType {
  prefilled: boolean;
}

const yourRolePageCommonTrackingProps = ({
  prefilled = false,
}: {
  prefilled?: boolean;
}) => ({
  currentPage: CURRENT_PAGE_YOUR_ROLE,
  prefilled,
});

export const trackDisplayYourRolePage = ({
  config,
  context,
  prefilled,
  variant,
}: YourRolePageCommonInputType & VariantType) => {
  sendViewEventWithCommonProps({
    eventName: `${CURRENT_PAGE_YOUR_ROLE}_displayed`,
    config,
    context,
    trackingProps: {
      ...yourRolePageCommonTrackingProps({ prefilled }),
      ...experimentTwoTrackingProps({ variant: variant ?? '1.0.0' }),
    },
  });
};

export const trackClickYourRolePageContinue = ({
  config,
  context,
  prefilled,
}: YourRolePageCommonInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_write_a_review_your_role_continue_pressed',
    config,
    context,
    trackingProps: {
      ...yourRolePageCommonTrackingProps({ prefilled }),
    },
  });
};

interface YourRoleSubSectionInputType {
  currentPageSubsection:
    | 'job_title'
    | 'location_selected'
    | 'work_type'
    | 'start_date'
    | 'end_date'
    | 'i_still_work_here';
}

export const trackClickYourRoleSubSection = ({
  config,
  context,
  currentPageSubsection,
  prefilled,
}: YourRolePageCommonInputType & YourRoleSubSectionInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_selection_pressed',
    config,
    context,
    trackingProps: {
      ...yourRolePageCommonTrackingProps({ prefilled }),
      currentPageSubsection,
    },
  });
};

export const trackValidationErrorYourRolePage = ({
  config,
  context,
  errorMessage,
  errorText,
  prefilled,
}: ValidationErrorCommonInputType & YourRolePageCommonInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_validation_error',
    config,
    context,
    trackingProps: {
      ...yourRolePageCommonTrackingProps({ prefilled }),
      errorMessage,
      errorText,
    },
  });
};

// Simplified Page

const simplifiedPageCommonTrackingProps = ({ variant }: VariantType) => ({
  currentPage: 'cr_write_a_review_simplified_page',
  hubbleExperiments: {
    '202505-single-page-review-flow': {
      id: 'ab1f0170-eb9b-421e-beb0-861c91743539',
      name: '202505-single-page-review-flow',
      participantStatus: 'enrolled',
      variant,
    },
  },
});

export const trackDisplaySimplifiedPage = ({
  config,
  context,
  variant,
}: CommonInputType & VariantType) => {
  sendViewEventWithCommonProps({
    eventName: 'cr_write_a_review_simplified_page_displayed',
    config,
    context,
    trackingProps: {
      ...simplifiedPageCommonTrackingProps({ variant }),
    },
  });
};

export const trackClickSimplifiedPageContinue = ({
  config,
  context,
  validation,
  variant,
}: CommonInputType & ContinueValidationCommonInputType & VariantType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_write_a_review_simplified_page_continue_pressed',
    config,
    context,
    trackingProps: {
      ...simplifiedPageCommonTrackingProps({ variant }),
      validation,
    },
  });
};

export const trackValidationErrorSimplifiedPage = ({
  config,
  context,
  errorMessage,
  errorText,
  variant,
}: ValidationErrorCommonInputType & CommonInputType & VariantType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_validation_error',
    config,
    context,
    trackingProps: {
      ...simplifiedPageCommonTrackingProps({ variant }),
      errorMessage,
      errorText,
    },
  });
};

interface SimplifiedPageSubSectionInputType {
  currentPageSubsection:
    | 'company_name'
    | 'overall_experience'
    | 'recommend_to_a_friend'
    | 'salary_rating'
    | 'job_title'
    | 'start_date'
    | 'end_date'
    | 'i_still_work_here'
    | 'work_type'
    | 'location_selected';
  prefilled?: boolean;
}

export const trackClickSimplifiedPageSubSection = ({
  config,
  context,
  currentPageSubsection,
  prefilled,
}: CommonInputType & SimplifiedPageSubSectionInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_selection_pressed',
    config,
    context,
    trackingProps: {
      currentPageSubsection,
      prefilled,
    },
  });
};

// Your Experience Page

export const trackClickYourExperiencePageContinue = ({
  config,
  context,
  validation,
}: CommonInputType & ContinueValidationCommonInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_write_a_review_your_experience_continue_pressed',
    config,
    context,
    trackingProps: {
      validation,
    },
  });
};

export const trackValidationErrorYourExperiencePage = ({
  config,
  context,
  errorMessage,
  errorText,
}: ValidationErrorCommonInputType & CommonInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_validation_error',
    config,
    context,
    trackingProps: {
      errorMessage,
      errorText,
    },
  });
};

interface YourExperienceSubSectionInputType {
  currentPageSubsection: 'recommend_to_a_friend';
}

export const trackClickYourExperienceSubSection = ({
  config,
  context,
  currentPageSubsection,
}: CommonInputType & YourExperienceSubSectionInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_selection_pressed',
    config,
    context,
    trackingProps: {
      currentPageSubsection,
    },
  });
};

// Your Salary Page

const yourSalaryPageCommonTrackingProps = () => ({
  currentPage: CURRENT_PAGE_YOUR_SALARY,
});

export const trackClickYourSalaryPageContinue = ({
  config,
  context,
  validation,
}: CommonInputType & ContinueValidationCommonInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_write_a_review_your_salary_continue_pressed',
    config,
    context,
    trackingProps: {
      ...yourSalaryPageCommonTrackingProps(),
      validation,
    },
  });
};

export const trackValidationErrorYourSalaryPage = ({
  config,
  context,
  errorMessage,
  errorText,
}: ValidationErrorCommonInputType & CommonInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_validation_error',
    config,
    context,
    trackingProps: {
      ...yourSalaryPageCommonTrackingProps(),
      errorMessage,
      errorText,
    },
  });
};

interface YourSalarySubSectionInputType {
  currentPageSubsection:
    | 'salary_rating'
    | 'pay_frequency'
    | 'salary_amount'
    | 'bonus';
}

export const trackClickYourSalarySubSection = ({
  config,
  context,
  currentPageSubsection,
}: CommonInputType & YourSalarySubSectionInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_selection_pressed',
    config,
    context,
    trackingProps: {
      ...yourSalaryPageCommonTrackingProps(),
      currentPageSubsection,
    },
  });
};

// Your Feedback Page

const yourFeedbackPageCommonTrackingProps = () => ({
  currentPage: 'cr_write_a_review_your_feedback',
});

interface YourFeedbackRatingPressInputType {
  currentPageSubsection:
    | 'career_development'
    | 'work_life_balance'
    | 'working_environment'
    | 'management'
    | 'perks_and_benefits'
    | 'diversity_and_equal_opportunity';
}

export const trackClickYourFeedbackRating = ({
  config,
  context,
  currentPageSubsection,
}: CommonInputType & YourFeedbackRatingPressInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_selection_pressed',
    config,
    context,
    trackingProps: {
      ...yourFeedbackPageCommonTrackingProps(),
      currentPageSubsection,
    },
  });
};

export const trackClickYourFeedbackPageContinue = ({
  config,
  context,
  validation,
}: CommonInputType & ContinueValidationCommonInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_write_a_review_your_feedback_continue_pressed',
    config,
    context,
    trackingProps: {
      ...yourFeedbackPageCommonTrackingProps(),
      validation,
    },
  });
};

export const trackValidationErrorYourFeedbackPage = ({
  config,
  context,
  errorMessage,
  errorText,
}: ValidationErrorCommonInputType & CommonInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_validation_error',
    config,
    context,
    trackingProps: {
      ...yourFeedbackPageCommonTrackingProps(),
      errorMessage,
      errorText,
    },
  });
};

// Submission Page

const submissionPageCommonTrackingProps = () => ({
  currentPage: CURRENT_PAGE_EVENT_SUBMISSION,
});

export const trackWriteAReviewSubmitted = ({
  config,
  context,
  variant,
}: CommonInputType & VariantType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_write_a_review_submitted',
    config,
    context,
    trackingProps: {
      ...submissionPageCommonTrackingProps(),
      ...experimentTrackingProps({ variant }),
    },
  });
};

export const trackClickSubmissionPageGoToCompanyProfile = ({
  config,
  context,
}: CommonInputType) => {
  sendLinkEventWithCommonProps({
    eventName: 'cr_write_a_review_complete_company_profile_pressed',
    config,
    context,
    trackingProps: {
      ...submissionPageCommonTrackingProps(),
    },
  });
};

export const CURRENT_PAGE_YOUR_SALARY = 'cr_write_a_review_your_salary';
export const CURRENT_PAGE_YOUR_ROLE = 'cr_write_a_review_your_role';
export const CURRENT_PAGE_YOUR_EXPERIENCE = 'cr_write_a_review_your_experience';
export const CURRENT_PAGE_COMPANY_INTERCEPT = 'cr_company_intercept';
export const CURRENT_PAGE_OVERALL = 'cr_write_a_review_overall_rating';
export const CURRENT_PAGE_EVENT_SUBMISSION = 'cr_write_a_review_completed';
export const CURRENT_PAGE_YOUR_FEEDBACK = 'cr_write_a_review_your_feedback';

// Experiment Two
export const CURRENT_PAGE_WORK_LIFE_BALANCE =
  'cr_write_a_review_work_life_balance';
export const CURRENT_PAGE_PERKS_AND_BENEFITS =
  'cr_write_a_review_perks_and_benefits';
// Your Salary is above
export const CURRENT_PAGE_CAREER_DEVELOPMENT =
  'cr_write_a_review_career_development';
export const CURRENT_PAGE_MANAGEMENT = 'cr_write_a_review_management';
export const CURRENT_PAGE_DIVERSITY_AND_EQUAL_OPPORTUNITY =
  'cr_write_a_review_diversity_and_equal_opportunity';
export const CURRENT_PAGE_WORKING_ENVIRONMENT =
  'cr_write_a_review_working_environment';

export const trackDisplayPage = ({
  config,
  context,
  currentPage,
  variant,
}: CommonInputType & {
  currentPage: string;
  variant?: string;
}) => {
  sendViewEventWithCommonProps({
    eventName: `${currentPage}_displayed`,
    config,
    context,
    trackingProps: {
      currentPage,
      ...experimentTwoTrackingProps({ variant: variant ?? '1.0.0' }),
    },
  });
};

export const trackSkipQuestionPressed = ({
  config,
  context,
  variant,
  currentPage,
}: CommonInputType &
  VariantType & {
    currentPage: string;
  }) => {
  sendViewEventWithCommonProps({
    eventName: `${currentPage}_skip_pressed`,
    config,
    context,
    trackingProps: {
      ...experimentTwoTrackingProps({ variant }),
      currentPage,
    },
  });
};

export const trackContinuePressed = ({
  config,
  context,
  validation,
  currentPage,
  variant,
}: CommonInputType &
  ContinueValidationCommonInputType & {
    currentPage: string;
    variant: string;
  }) => {
  sendLinkEventWithCommonProps({
    eventName: `${currentPage}_continue_pressed`,
    config,
    context,
    trackingProps: {
      ...experimentTwoTrackingProps({ variant }),
      validation,
      currentPage,
    },
  });
};
